/* eslint-disable no-underscore-dangle */
//@ts-nocheck
import { ChartData, ChartOptions } from "chart.js";
import React, { useCallback, useContext, useMemo } from "react";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { SideResponse } from "../../codegen-api";
import { SettingsContext } from "../../contexts/SettingsContext";
import {
  calculateBreakevenForOption,
  calculateProfitPerOption,
} from "../../utils/instruments";
import { IProfitChartProps } from "./types";
import { buildChartData } from "./chartData";
import {
  computePriceRange,
  drawLabelImpl,
  drawPricePointImpl,
  findNeighbourPointImpl,
} from "./helpers";

const ProfitChart: React.FC<IProfitChartProps> = ({
  asset,
  strike,
  price,
  premium,
  isPut,
  orderDirection,
  onHover,
}) => {
  const { t } = useTranslation("app", { keyPrefix: "ProfitChart" });
  // Subscribe so the chart re-renders (and redraws with fresh --color-up/down)
  // when the user changes the direction scheme.
  const { directionScheme, customDirection } = useContext(SettingsContext);

  const calculateProfit = useCallback(
    (p: number) => {
      const profit = calculateProfitPerOption(
        isPut,
        p,
        strike,
        premium,
        orderDirection
      );
      // eslint-disable-next-line no-compare-neg-zero
      return profit === 0 ? Math.abs(profit) : profit;
    },
    [isPut, orderDirection, premium, strike]
  );

  const breakeven = useMemo(
    () => calculateBreakevenForOption(isPut, strike, premium),
    [isPut, premium, strike]
  );

  const findNeighbourPoint = useCallback(findNeighbourPointImpl, []);

  const priceRange = useMemo(
    () => computePriceRange(breakeven, premium, strike),
    [breakeven, premium, strike]
  );

  const drawLabel = useCallback(drawLabelImpl, []);

  const drawPricePoint = useCallback(
    (chart: any, pricePoint: number, drawIndex: number, y: number) =>
      drawPricePointImpl(chart, pricePoint, drawIndex, y, {
        calculateProfit,
        drawLabel,
        t,
        asset,
      }),
    [asset, calculateProfit, drawLabel, t, directionScheme, customDirection]
  );

  const ticks = useMemo(() => {
    if (orderDirection === SideResponse.Sell) {
      return {
        min: isPut
          ? calculateProfit(priceRange[0])
          : calculateProfit(priceRange[priceRange.length - 1]),
        max: Math.abs(
          isPut
            ? calculateProfit(priceRange[0])
            : calculateProfit(priceRange[priceRange.length - 1])
        ),
      };
    }
    return {
      min: isPut
        ? -calculateProfit(priceRange[0])
        : -calculateProfit(priceRange[priceRange.length - 1]),
      max: isPut
        ? calculateProfit(priceRange[0])
        : calculateProfit(priceRange[priceRange.length - 1]),
    };
  }, [calculateProfit, isPut, orderDirection, priceRange]);

  const options = useMemo(
    (): ChartOptions => ({
      maintainAspectRatio: false,
      legend: { display: false },
      layout: { padding: { top: 24 } },
      scales: {
        yAxes: [
          {
            display: false,
            ticks,
          },
        ],
        xAxes: [{ display: false }],
      },
      animation: { duration: 0 },
      hover: { animationDuration: 0, intersect: false },
      tooltips: {
        enabled: false,
        intersect: false,
        mode: "nearest",
      },
      onHover: (_: any, elements: any) => {
        if (elements && elements.length) {
          onHover(priceRange[elements[0]._index]);
          return;
        }
        onHover(undefined);
      },
    }),
    [ticks, onHover, priceRange]
  );

  const getData = useCallback(
    (canvas: any): ChartData =>
      buildChartData(canvas, {
        priceRange,
        breakeven,
        price,
        calculateProfit,
        findNeighbourPoint,
      }),
    [
      breakeven,
      calculateProfit,
      findNeighbourPoint,
      price,
      priceRange,
      directionScheme,
      customDirection,
    ]
  );

  const chart = useMemo(() => {
    const neighbourPointToStrike = findNeighbourPoint(priceRange, strike);
    const closestStrikePoint =
      Math.abs(strike - neighbourPointToStrike[0]) <
      Math.abs(strike - neighbourPointToStrike[1])
        ? neighbourPointToStrike[0]
        : neighbourPointToStrike[1];
    const closestStrikeIndex = priceRange.findIndex(
      (p) => p === closestStrikePoint
    );

    return (
      <Line
        //@ts-ignore
        type="line"
        data={getData}
        options={options}
        //   plugins={[
        //     {
        //       afterDraw: (c: any) => {
        //         // eslint-disable-next-line prefer-destructuring
        //         const ctx: CanvasRenderingContext2D = c.chart.ctx;
        //         // Get breakeven datasaet because of stability over every point
        //         const datasetIndex = c.chart.data.datasets.findIndex(
        //           (dataset: any) => dataset.label === "breakeven"
        //         );
        //         const meta = c.chart.getDatasetMeta(datasetIndex);
        //         const leftX = c.chart.scales["x-axis-0"].left;
        //         const rightX = c.chart.scales["x-axis-0"].right;
        //         const topY = c.chart.scales["y-axis-0"].top;
        //         const bottomY = c.chart.scales["y-axis-0"].bottom - 1;
        //         const strikeElement = meta.data[closestStrikeIndex];
        //         const strikeX = strikeElement._view.x;

        //         // Draw borders
        //         ctx.save();
        //         ctx.beginPath();
        //         ctx.moveTo(leftX, bottomY);
        //         ctx.lineTo(rightX, bottomY);
        //         ctx.lineWidth = 1;
        //         ctx.strokeStyle = BORDER_COLORS.one;
        //         ctx.stroke();
        //         ctx.restore();

        //         ctx.save();
        //         ctx.beginPath();
        //         ctx.moveTo(leftX, bottomY - SPACING.five);
        //         ctx.lineTo(rightX, bottomY - SPACING.five);
        //         ctx.lineWidth = 1;
        //         ctx.strokeStyle = BORDER_COLORS.one;
        //         ctx.stroke();
        //         ctx.restore();

        //         // DRAW THE STRIKE LABEL
        //         const strikeTextY = bottomY - SPACING.three;
        //         drawLabel(
        //           ctx,
        //           t("strike"),
        //           currency(strike).format(),
        //           TEXT_COLORS.one,
        //           strikeX,
        //           strikeTextY
        //         );

        //         // Draw strike point
        //         ctx.save();
        //         ctx.beginPath();
        //         ctx.setLineDash([1, 1]);
        //         ctx.moveTo(strikeX, topY);
        //         ctx.lineTo(strikeX, bottomY - SPACING.five);
        //         ctx.lineWidth = 1;
        //         ctx.strokeStyle = COLORS.white.four;
        //         ctx.stroke();
        //         ctx.restore();

        //         // Draw price point
        //         const priceDatasetIndex = c.chart.data.datasets.findIndex(
        //           (dataset: any) => dataset.label === "price"
        //         );
        //         const priceDataset = c.chart.data.datasets[priceDatasetIndex];

        //         const priceIndex = priceDataset.data.findIndex(
        //           (data: any) => data !== null
        //         );

        //         drawPricePoint(
        //           c,
        //           priceDataset.data[priceIndex],
        //           priceIndex,
        //           bottomY - SPACING.five
        //         );
        //       },
        //     },
        //   ]}
      />
    );
  }, [
    drawPricePoint,
    getData,
    findNeighbourPoint,
    priceRange,
    strike,
    options,
    drawLabel,
    t,
  ]);

  return chart;
};

export default ProfitChart;
